import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse
} from '@mui/material'
import {
  Download,
  CloudUpload,
  ExpandMore,
  ExpandLess,
  FactCheck,
  Send
} from '@mui/icons-material'
import * as actions from '../../actions.es6.js'
import { canImportRatingWorkbook } from '../../../../lib/access.es6.js'

const navy = '#173E4B'
const ALLOWED_EXT = ['xlsx']
// Mirrors the backend default (RATING_IMPORT_MAX_FILE_BYTES) — defense in
// depth only; the server is the real limit.
const MAX_FILE_BYTES = 5 * 1024 * 1024

const TH = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  bgcolor: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB'
}

function validateFile(file) {
  const ext = file.name
    .split('.')
    .pop()
    .toLowerCase()
  if (!ALLOWED_EXT.includes(ext)) {
    return `Unsupported file type ".${ext}". Upload the generated .xlsx workbook.`
  }
  if (file.size > MAX_FILE_BYTES) {
    return `File exceeds the ${MAX_FILE_BYTES / 1024 / 1024}MB limit.`
  }
  return null
}

// One workbook per product: download the generated rate-card template, fill
// it in, validate (dry run — no writes), then submit. A clean submit always
// opens a RatingBulkImport change request (rating changes are unconditionally
// four-eyes gated) — nothing is materialized until a different user approves
// it in the Rating queue.
function RatingImportPanel(props) {
  const { dispatch, productGuid } = props
  const data = props.genericProductsData || {}
  const [expanded, setExpanded] = useState(false)

  // This state is global (not keyed by product) and this panel unmounts on
  // every tab switch (ViewProduct renders one tab at a time), so a plain
  // "reset on mount" would wipe an in-progress dry-run/submit every time the
  // actuary tabs away and back — not just when they navigate to a different
  // product. Compare against the productGuid the state was stamped with
  // (set in SELECT_RATING_WORKBOOK) and only reset on a genuine mismatch.
  useEffect(() => {
    if (
      data.ratingImportProductGuid &&
      data.ratingImportProductGuid !== productGuid
    ) {
      dispatch(actions.resetRatingImport())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGuid, data.ratingImportProductGuid])

  // Mirrors the backend @Roles guard (CREATE_PRODUCT) on every
  // RatingImportController route — hooks above must run first regardless.
  if (!canImportRatingWorkbook()) return null

  const file = data.ratingImportWorkbook
  const report = data.ratingImportReport
  const result = data.ratingImportResult
  const fileError = file ? validateFile(file) : null
  const canSubmit =
    report && report.valid && (report.errors || []).length === 0 && !result

  const handleFileChange = e => {
    const f = e.target.files && e.target.files[0]
    if (f) dispatch(actions.selectRatingWorkbook({ productGuid, file: f }))
  }

  const handleValidate = () => {
    if (file && !fileError) {
      dispatch(actions.validateRatingWorkbookRequest({ productGuid, file }))
    }
  }

  const handleSubmit = () => {
    if (file && canSubmit) {
      dispatch(actions.importRatingWorkbookRequest({ productGuid, file }))
    }
  }

  const handleReset = () => dispatch(actions.resetRatingImport())

  const report_ = result || report // the submit result carries the same report shape

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#1f2937' }}
            >
              Bulk Import
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Price this product's whole rate card from one spreadsheet instead
              of one rating table/node at a time.
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setExpanded(x => !x)}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            sx={{ textTransform: 'none', color: navy }}
          >
            {expanded ? 'Hide' : 'Import from spreadsheet'}
          </Button>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                data.ratingImportTemplateLoader ? (
                  <CircularProgress size={16} />
                ) : (
                  <Download />
                )
              }
              disabled={data.ratingImportTemplateLoader}
              onClick={() =>
                dispatch(actions.downloadRatingTemplateRequest(productGuid))
              }
              sx={{
                textTransform: 'none',
                color: navy,
                borderColor: navy,
                mb: 2
              }}
            >
              Download Template
            </Button>

            {data.ratingImportTemplateError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {data.ratingImportTemplateError.message ||
                  data.ratingImportTemplateError}
              </Alert>
            )}

            {!result && (
              <Card
                variant="outlined"
                sx={{ p: 2, textAlign: 'center', mb: 2 }}
              >
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id={`rating-import-file-input-${productGuid}`}
                />
                <label
                  htmlFor={`rating-import-file-input-${productGuid}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <CloudUpload sx={{ fontSize: 36, color: navy, mb: 1 }} />
                  <Typography variant="body1">
                    {file ? file.name : 'Click to select the filled workbook'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    .xlsx, max {MAX_FILE_BYTES / 1024 / 1024}MB
                  </Typography>
                </label>
              </Card>
            )}

            {fileError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {fileError}
              </Alert>
            )}

            {!result && file && !fileError && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    data.ratingImportValidateLoader ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FactCheck />
                    )
                  }
                  disabled={data.ratingImportValidateLoader}
                  onClick={handleValidate}
                  sx={{ textTransform: 'none', color: navy, borderColor: navy }}
                >
                  {data.ratingImportValidateLoader
                    ? 'Validating…'
                    : 'Validate (dry run)'}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    data.ratingImportSubmitLoader ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Send />
                    )
                  }
                  disabled={!canSubmit || data.ratingImportSubmitLoader}
                  onClick={handleSubmit}
                  sx={{
                    textTransform: 'none',
                    bgcolor: navy,
                    '&:hover': { bgcolor: '#0f2d38' }
                  }}
                >
                  {data.ratingImportSubmitLoader
                    ? 'Submitting…'
                    : 'Submit for Approval'}
                </Button>
                <Button
                  size="small"
                  onClick={handleReset}
                  sx={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
              </Box>
            )}

            {data.ratingImportValidateError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {data.ratingImportValidateError.message ||
                  data.ratingImportValidateError}
              </Alert>
            )}
            {data.ratingImportSubmitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {data.ratingImportSubmitError.message ||
                  data.ratingImportSubmitError}
              </Alert>
            )}

            {result && result.change_request_guid && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Submitted as change request{' '}
                <strong>{result.change_request_guid}</strong> — pending approval
                in the Rating queue. Nothing is priced until a different user
                approves it.
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={handleReset}
                    sx={{ textTransform: 'none' }}
                  >
                    Import another workbook
                  </Button>
                </Box>
              </Alert>
            )}

            {report_ && (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}
                >
                  <Chip
                    size="small"
                    label={report_.valid ? 'Valid' : 'Invalid'}
                    sx={{
                      fontWeight: 600,
                      bgcolor: report_.valid ? '#F0FDF4' : '#FEF2F2',
                      color: report_.valid ? '#15803D' : '#B91C1C'
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${report_.table_count} table${
                      report_.table_count !== 1 ? 's' : ''
                    }`}
                    sx={{ bgcolor: '#eef2f4', color: navy }}
                  />
                  <Chip
                    size="small"
                    label={`${report_.node_count} node${
                      report_.node_count !== 1 ? 's' : ''
                    }`}
                    sx={{ bgcolor: '#eef2f4', color: navy }}
                  />
                </Box>

                {(report_.packages || []).length > 0 && (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={TH}>Package</TableCell>
                          <TableCell sx={TH}>Kind</TableCell>
                          <TableCell sx={TH}>Targets</TableCell>
                          <TableCell sx={TH} align="right">
                            Nodes
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report_.packages.map((p, i) => (
                          <TableRow key={i}>
                            <TableCell>{p.package_code || '—'}</TableCell>
                            <TableCell>{p.kind}</TableCell>
                            <TableCell>
                              {(p.targets || []).join(', ')}
                            </TableCell>
                            <TableCell align="right">{p.node_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {(report_.errors || []).length > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: '#B91C1C',
                        mb: 1
                      }}
                    >
                      Errors — fix these in the sheet and re-upload
                    </Typography>
                    <TableContainer sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={TH}>Sheet</TableCell>
                            <TableCell sx={TH}>Row</TableCell>
                            <TableCell sx={TH}>Entity</TableCell>
                            <TableCell sx={TH}>Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report_.errors.map((e, i) => (
                            <TableRow key={i}>
                              <TableCell>{e.sheet}</TableCell>
                              <TableCell>{e.row}</TableCell>
                              <TableCell>{e.entity || '—'}</TableCell>
                              <TableCell sx={{ color: '#B91C1C' }}>
                                {e.message}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {(report_.warnings || []).length > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: '#B45309',
                        mb: 1
                      }}
                    >
                      Warnings — parsed, but worth a second look
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={TH}>Sheet</TableCell>
                            <TableCell sx={TH}>Row</TableCell>
                            <TableCell sx={TH}>Entity</TableCell>
                            <TableCell sx={TH}>Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report_.warnings.map((w, i) => (
                            <TableRow key={i}>
                              <TableCell>{w.sheet}</TableCell>
                              <TableCell>
                                {w.row != null ? w.row : '—'}
                              </TableCell>
                              <TableCell>{w.entity || '—'}</TableCell>
                              <TableCell sx={{ color: '#B45309' }}>
                                {w.message}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export { RatingImportPanel }

export default connect(state => ({
  genericProductsData: state.genericProducts
}))(RatingImportPanel)
